import React from 'react';
import { FiCheck, FiStar, FiZap, FiAward } from 'react-icons/fi';
import styles from './SubscriptionPlans.module.css';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  name: string;
  price: number;
  currency: string;
  period: string;
  description: string;
  features: PlanFeature[];
  popular?: boolean;
  icon: React.ReactNode;
}

const plans: Plan[] = [
  {
    name: 'Free',
    price: 0,
    currency: 'R',
    period: 'month',
    description: 'Perfect for small businesses getting started',
    icon: <FiStar size={24} />,
    features: [
      { text: 'Up to 5 invoices per month', included: true },
      { text: 'Basic client management', included: true },
      { text: 'Standard templates', included: true },
      { text: 'Email support', included: true },
      { text: 'Basic reporting', included: true },
      { text: 'Advanced analytics', included: false },
      { text: 'Priority support', included: false },
      { text: 'Custom branding', included: false },
    ]
  },
  {
    name: 'Professional',
    price: 169,
    currency: 'R',
    period: 'month',
    description: 'Ideal for growing businesses',
    icon: <FiZap size={24} />,
    popular: true,
    features: [
      { text: 'Up to 50 invoices per month', included: true },
      { text: 'Advanced client management', included: true },
      { text: 'Premium templates', included: true },
      { text: 'Priority email support', included: true },
      { text: 'Advanced reporting & analytics', included: true },
      { text: 'Custom branding', included: true },
      { text: 'API access', included: true },
      { text: 'Dedicated account manager', included: false },
    ]
  },
  {
    name: 'Enterprise',
    price: 279,
    currency: 'R',
    period: 'month',
    description: 'For established businesses with complex needs',
    icon: <FiAward size={24} />,
    features: [
      { text: 'Unlimited invoices', included: true },
      { text: 'Enterprise client management', included: true },
      { text: 'Custom templates & workflows', included: true },
      { text: '24/7 phone & email support', included: true },
      { text: 'Advanced analytics & insights', included: true },
      { text: 'Full custom branding', included: true },
      { text: 'API access & webhooks', included: true },
      { text: 'Dedicated account manager', included: true },
    ]
  }
];

export function SubscriptionPlans() {
  const formatCurrency = (amount: number, currency: string) => {
    if (amount === 0) return 'Free';
    return `${currency}${amount.toLocaleString('en-ZA')}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Choose Your Plan</h2>
        <p className={styles.subtitle}>
          Select the perfect plan for your business needs. All prices in South African Rand (R)
        </p>
      </div>

      <div className={styles.plansGrid}>
        {plans.map((plan) => (
          <div 
            key={plan.name} 
            className={`${styles.planCard} ${plan.popular ? styles.popular : ''}`}
          >
            {plan.popular && (
              <div className={styles.popularBadge}>Most Popular</div>
            )}
            
            <div className={styles.planHeader}>
              <div className={styles.planIcon}>
                {plan.icon}
              </div>
              <h3 className={styles.planName}>{plan.name}</h3>
              <p className={styles.planDescription}>{plan.description}</p>
            </div>

            <div className={styles.planPricing}>
              <div className={styles.price}>
                {formatCurrency(plan.price, plan.currency)}
                {plan.price > 0 && (
                  <span className={styles.period}>/{plan.period}</span>
                )}
              </div>
            </div>

            <ul className={styles.featuresList}>
              {plan.features.map((feature, index) => (
                <li 
                  key={index} 
                  className={`${styles.feature} ${feature.included ? styles.included : styles.excluded}`}
                >
                  <span className={styles.featureIcon}>
                    {feature.included ? <FiCheck size={16} /> : '×'}
                  </span>
                  <span className={styles.featureText}>{feature.text}</span>
                </li>
              ))}
            </ul>

            <button className={`${styles.planButton} ${plan.popular ? styles.popularButton : ''}`}>
              {plan.price === 0 ? 'Get Started Free' : 'Choose Plan'}
            </button>
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <p className={styles.footerText}>
          All plans include a 14-day free trial. No credit card required to start.
        </p>
        <p className={styles.currencyNote}>
          * Prices are in South African Rand (R) and may be subject to VAT
        </p>
      </div>
    </div>
  );
}
